const HEIGHT_MESSAGE = 'piyonna-article-html-embed:height';
const HEIGHT_REQUEST = 'piyonna-article-html-embed:request';
const ADD_TO_CART_MESSAGE = 'piyonna-article-html-embed:add-to-cart';

// cart:update 신호의 출처 표시. cart-icon 은 출처가 'product-form-component' 일 때만
// 기존 배지 숫자에 더하므로, 다른 값을 쓰면 itemCount 를 그대로 덮어쓴다(= 총개수를 넘긴다).
const SOURCE = 'piyonna-article-html-embed';

const ADDED_LABEL = 'Added ✓';
const ADDED_LABEL_DURATION = 1800;

class PiyonnaArticleHtmlEmbed extends HTMLElement {
  /** 처리 중인 상품 옵션 id — 같은 버튼 연타로 중복 담기는 것을 막는다. @type {Set<number>} */
  #pending = new Set();

  connectedCallback() {
    const iframe = this.querySelector(':scope > iframe');
    if (!iframe) return;

    const onMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      const data = event.data;
      if (!data) return;

      if (data.type === HEIGHT_MESSAGE) {
        const height = Number(data.height);
        if (!Number.isFinite(height) || height <= 0) return;
        iframe.style.height = height + 'px';
        return;
      }

      // 글 본문(iframe)은 "이 상품 담아줘"만 알린다. 실제 담기와 화면 갱신은 여기(테마)에서
      // 처리한다 — iframe 안에서 부모 DOM을 더듬으면 배지·서랍이 갱신되지 않는다.
      if (data.type === ADD_TO_CART_MESSAGE) {
        this.#addToCart(iframe, data);
      }
    };

    window.addEventListener('message', onMessage);

    // 부모 리스너가 붙은 뒤 iframe에 높이를 명시적으로 요청 → postMessage race 제거
    const requestHeight = () => {
      iframe.contentWindow?.postMessage({ type: HEIGHT_REQUEST }, '*');
    };

    // iframe이 이미 로드됐든(즉시 요청) 아직 안 됐든(load 시 요청) 양쪽 모두 대응
    iframe.addEventListener('load', requestHeight);
    if (iframe.contentWindow) requestHeight();
  }

  /**
   * 글 본문에서 온 담기 요청을 테마 규약대로 처리한다.
   * @param {HTMLIFrameElement} iframe
   * @param {{ variantId?: unknown, quantity?: unknown }} payload
   */
  async #addToCart(iframe, payload) {
    const variantId = Number(payload.variantId);
    if (!Number.isInteger(variantId) || variantId <= 0) return;
    if (this.#pending.has(variantId)) return;

    const requested = Number(payload.quantity);
    const quantity = Number.isInteger(requested) && requested > 0 ? requested : 1;
    const buttons = this.#buttonsFor(iframe, variantId);

    this.#pending.add(variantId);
    for (const button of buttons) button.disabled = true;

    try {
      // 갱신이 필요한 장바구니 섹션을 함께 요청해 최신 HTML을 응답으로 받는다
      // (테마 상품폼이 쓰는 방식과 동일 — product-form.js 참고).
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);

      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const added = await response.json();
      // 품절·수량 초과 등의 실패는 에러 응답이 아니라 본문의 status로 온다
      if (added.status) throw new Error(added.description || added.message || 'cart add failed');

      // 에어브릿지 담기(A2) — items 로 보냈으니 응답도 items 배열이다 (assets/airbridge.js)
      window.GBAirbridge?.addedToCart(added.items?.[0], quantity);

      // 담기 응답에는 장바구니 총개수가 없어서 배지용으로 따로 조회한다
      // (테마 상품폼도 같은 이유로 /cart.js를 한 번 더 부른다).
      const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
      const cart = await (await fetch(cartUrl)).json();

      // 이 신호 하나로 cart-items-component(서랍 내용) · cart-icon(배지) ·
      // cart-drawer-component(PC 서랍 열기 / 모바일 토스트)가 각자 반응한다.
      const cartUpdate = new Event('cart:update', { bubbles: true });
      cartUpdate.detail = {
        resource: cart,
        sourceId: SOURCE,
        data: {
          source: SOURCE,
          itemCount: cart.item_count,
          variantId: String(variantId),
          sections: added.sections,
        },
      };
      document.dispatchEvent(cartUpdate);

      this.#markAdded(buttons);
    } catch (error) {
      // classic script 라 @theme/utilities 를 import 할 수 없다. utilities.js 는
      // scripts.liquid 의 무조건 로드 모듈들이 import 하므로 전 페이지에서 실행되지만,
      // 로드 순서에 기대지 않도록 optional chaining 으로 접근한다.
      window.Theme?.utilities?.logError?.('piyonna-article-html-embed:addToCart', error);
      window.showToast?.(error instanceof Error ? error.message : String(error), 3000);
      for (const button of buttons) button.disabled = false;
    } finally {
      this.#pending.delete(variantId);
    }
  }

  /**
   * 글 본문(iframe) 안에서 해당 상품의 담기 버튼을 찾는다. 같은 상품이 여러 곳에 노출될 수
   * 있어 전부 반환한다. iframe 문서에 접근할 수 없으면 버튼 표시만 생략되고 담기는 정상 동작한다.
   * @param {HTMLIFrameElement} iframe
   * @param {number} variantId
   * @returns {HTMLButtonElement[]}
   */
  #buttonsFor(iframe, variantId) {
    try {
      const doc = iframe.contentDocument;
      if (!doc) return [];
      return Array.from(doc.querySelectorAll(`.piy-add-cart[data-variant-id="${variantId}"]`));
    } catch (_) {
      return [];
    }
  }

  /**
   * 담기 성공을 버튼에 잠깐 표시한 뒤 원래 상태로 되돌린다.
   * @param {HTMLButtonElement[]} buttons
   */
  #markAdded(buttons) {
    for (const button of buttons) {
      const original = button.dataset.piyOriginalLabel ?? button.textContent ?? '';
      button.dataset.piyOriginalLabel = original;
      button.textContent = ADDED_LABEL;
      button.classList.add('piy-added');

      setTimeout(() => {
        button.textContent = original;
        button.classList.remove('piy-added');
        button.disabled = false;
      }, ADDED_LABEL_DURATION);
    }
  }
}

if (!customElements.get('piyonna-article-html-embed')) {
  customElements.define('piyonna-article-html-embed', PiyonnaArticleHtmlEmbed);
}
